import * as AccordionPrimitive from "@radix-ui/react-accordion";
import * as React from "react";

import { ChevronRight } from "lucide-react";
import { cn } from "../../libs/utils";

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn("border-b", className)}
    {...props}
  />
));
AccordionItem.displayName = "AccordionItem";

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
    classNameChevron?: string;
    backgroundClassName?: string;
  }
>(
  (
    { className, children, classNameChevron, backgroundClassName, ...props },
    ref
  ) => {
    const hasCustomBackground = !!backgroundClassName;

    return (
      <AccordionPrimitive.Header className="flex">
        <AccordionPrimitive.Trigger
          ref={ref}
          className={cn(
            hasCustomBackground
              ? "group flex justify-between items-start w-full px-[18px] py-[10px] rounded-[9px]"
              : "group flex justify-between items-start w-full px-[18px] py-[10px] rounded-[9px]",
            backgroundClassName ?? "bg-white",
            className
          )}
          {...props}
        >
          <div
            className={cn(
              hasCustomBackground
                ? "w-full flex items-center gap-2 flex-1 text-left pr-4"
                : "w-full flex items-center gap-2 flex-1 text-left pr-4"
            )}
          >
            <ChevronRight
              className={cn(
                "h-4 w-4 shrink-0 transition-transform duration-300 ease-in-out group-data-[state=open]:rotate-90",
                classNameChevron
              )}
            />
            {children}
          </div>
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>
    );
  }
);
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
    {...props}
  >
    <div className={cn("pb-4 pt-0", className)}>{children}</div>
  </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
